import React, { useEffect, useRef, useState } from "react";
import { mutate } from "swr";
import { api, photoUrl, logoUrl, faviconUrl } from "@/lib/api";
import { toast } from "sonner";
import { Save, Image as ImageIcon, Check, Upload } from "lucide-react";

export default function TextsTab() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => { load(); loadPhotos(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/content");
      setContent(data);
    } catch (e) { toast.error("No se pudo cargar el contenido"); }
    finally { setLoading(false); }
  };
  const loadPhotos = async () => {
    try { const { data } = await api.get("/admin/photos"); setPhotos(data); }
    catch (e) { /* silent */ }
  };
  const set = (path, val) => {
    setContent((c) => {
      const next = JSON.parse(JSON.stringify(c));
      const parts = path.split(".");
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]];
      obj[parts[parts.length - 1]] = val;
      return next;
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content", { content });
      mutate("/public/home");
      toast.success("Contenido guardado");
    } catch (e) { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (loading || !content) return <div className="text-[#7da3ff] mono">Cargando textos…</div>;

  return (
    <div className="space-y-8 max-w-5xl">
      <Header title="Textos del sitio" subtitle="Editá los textos principales que se muestran en la web." onSave={save} saving={saving} />

      <Section title="Marca">
        <LogoUpload
          currentFilename={content.brand?.logo_filename}
          onUploaded={(fn) => set("brand.logo_filename", fn)}
        />
        <FaviconUpload
          currentFilename={content.brand?.favicon_filename}
          onUploaded={(fn) => set("brand.favicon_filename", fn)}
        />
        <Field label="Nombre" value={content.brand?.name || ""} onChange={(v) => set("brand.name", v)} />
        <Field label="Badge superior" value={content.brand?.badge || ""} onChange={(v) => set("brand.badge", v)} />
        <Field label="Tagline" value={content.brand?.tagline || ""} onChange={(v) => set("brand.tagline", v)} />
      </Section>

      <Section title="Hero">
        <HeroPhotoPicker
          photos={photos}
          current={content.hero?.image_filename}
          onChange={(fn) => set("hero.image_filename", fn)}
          open={pickerOpen}
          setOpen={setPickerOpen}
        />
        <Field label="Título (antes)" value={content.hero?.title_pre || ""} onChange={(v) => set("hero.title_pre", v)} />
        <Field label="Título (énfasis)" value={content.hero?.title_em || ""} onChange={(v) => set("hero.title_em", v)} />
        <Field label="Título (después)" value={content.hero?.title_post || ""} onChange={(v) => set("hero.title_post", v)} />
        <TextArea label="Subtítulo" value={content.hero?.subtitle || ""} onChange={(v) => set("hero.subtitle", v)} />
        <Field label="CTA primario" value={content.hero?.cta_primary || ""} onChange={(v) => set("hero.cta_primary", v)} />
        <Field label="CTA secundario" value={content.hero?.cta_secondary || ""} onChange={(v) => set("hero.cta_secondary", v)} />
      </Section>

      <Section title="Estadísticas (3)">
        {(content.stats || []).map((s, i) => (
          <div key={i} className="grid sm:grid-cols-2 gap-3">
            <Field label={`Valor ${i+1}`} value={s.value} onChange={(v) => set(`stats.${i}.value`, v)} />
            <Field label={`Label ${i+1}`} value={s.label} onChange={(v) => set(`stats.${i}.label`, v)} />
          </div>
        ))}
      </Section>

      <Section title="Nosotros">
        <Field label="Kicker" value={content.about?.kicker || ""} onChange={(v) => set("about.kicker", v)} />
        <Field label="Título" value={content.about?.title || ""} onChange={(v) => set("about.title", v)} />
        <TextArea label="Cuerpo" value={content.about?.body || ""} onChange={(v) => set("about.body", v)} />
        <TextArea label="Quote" value={content.about?.quote || ""} onChange={(v) => set("about.quote", v)} />
        <Field label="Autor del quote" value={content.about?.quote_author || ""} onChange={(v) => set("about.quote_author", v)} />
      </Section>

      <Section title="Broadcasting">
        <Field label="Kicker" value={content.broadcasting?.kicker || ""} onChange={(v) => set("broadcasting.kicker", v)} />
        <Field label="Título" value={content.broadcasting?.title || ""} onChange={(v) => set("broadcasting.title", v)} />
        <TextArea label="Cuerpo" value={content.broadcasting?.body || ""} onChange={(v) => set("broadcasting.body", v)} />
      </Section>

      <Section title="Galería">
        <Field label="Kicker" value={content.gallery?.kicker || ""} onChange={(v) => set("gallery.kicker", v)} />
        <Field label="Título" value={content.gallery?.title || ""} onChange={(v) => set("gallery.title", v)} />
        <TextArea label="Cuerpo" value={content.gallery?.body || ""} onChange={(v) => set("gallery.body", v)} />
      </Section>

      <Section title="Contacto">
        <Field label="Kicker" value={content.contact?.kicker || ""} onChange={(v) => set("contact.kicker", v)} />
        <Field label="Título" value={content.contact?.title || ""} onChange={(v) => set("contact.title", v)} />
        <TextArea label="Cuerpo" value={content.contact?.body || ""} onChange={(v) => set("contact.body", v)} />
        <Field label="Email" value={content.contact?.email || ""} onChange={(v) => set("contact.email", v)} />
        <Field label="WhatsApp (solo números)" value={content.contact?.whatsapp || ""} onChange={(v) => set("contact.whatsapp", v)} />
        <Field label="WhatsApp (visible)" value={content.contact?.whatsapp_display || ""} onChange={(v) => set("contact.whatsapp_display", v)} />
        <Field label="Dirección línea 1" value={content.contact?.address_line1 || ""} onChange={(v) => set("contact.address_line1", v)} />
        <Field label="Dirección línea 2" value={content.contact?.address_line2 || ""} onChange={(v) => set("contact.address_line2", v)} />
        <Field label="URL soporte" value={content.contact?.support_url || ""} onChange={(v) => set("contact.support_url", v)} />
        <Field label="URL cloud" value={content.contact?.cloud_url || ""} onChange={(v) => set("contact.cloud_url", v)} />
      </Section>

      <div className="sticky bottom-3 z-10">
        <button onClick={save} disabled={saving} className="btn-primary px-6 py-3 rounded-xl inline-flex items-center gap-2 font-medium">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
        </button>
      </div>
    </div>
  );
}

export function Header({ title, subtitle, onSave, saving }) {
  return (
    <div className="flex items-end justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-3xl font-bold text-white">{title}</h1>
        {subtitle && <p className="text-[#9bb0e0] mt-2">{subtitle}</p>}
      </div>
      {onSave && (
        <button onClick={onSave} disabled={saving} className="btn-primary px-5 py-2.5 rounded-lg inline-flex items-center gap-2 text-sm font-medium">
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar"}
        </button>
      )}
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <div className="glass rounded-2xl p-6">
      <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#7da3ff] mb-4">{title}</div>
      <div className="grid gap-4">{children}</div>
    </div>
  );
}

export function Field({ label, value, onChange, ...props }) {
  return (
    <div>
      <label className="block text-xs text-[#9bb0e0] mb-1.5">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} {...props} className="w-full bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#5fa0ff]" />
    </div>
  );
}

export function TextArea({ label, value, onChange, rows=3 }) {
  return (
    <div>
      <label className="block text-xs text-[#9bb0e0] mb-1.5">{label}</label>
      <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} className="w-full bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#5fa0ff]" />
    </div>
  );
}


function HeroPhotoPicker({ photos, current, onChange, open, setOpen }) {
  const currentPhoto = photos.find((p) => p.filename === current);
  return (
    <div>
      <label className="block text-xs text-[#9bb0e0] mb-2">Foto de portada (hero)</label>
      <div className="flex items-stretch gap-3 flex-wrap">
        <div className="w-44 h-28 bg-[#0a1020] border border-line overflow-hidden flex items-center justify-center relative">
          {currentPhoto ? (
            <img src={photoUrl(currentPhoto)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="text-mute text-xs flex flex-col items-center gap-1">
              <ImageIcon className="w-5 h-5" />
              <span>Sin foto seleccionada</span>
            </div>
          )}
        </div>
        <div className="flex flex-col justify-between">
          <div>
            <div className="text-fg text-sm font-medium">{currentPhoto?.title || (current ? current : "Usando foto por defecto")}</div>
            <div className="text-mute text-[11px] mono mt-1">{currentPhoto?.filename || "rack-vivo-01.jpg (fallback)"}</div>
          </div>
          <div className="flex gap-2 mt-2">
            <button type="button" onClick={() => setOpen(true)} className="btn-primary px-3 py-1.5 text-xs rounded-sm inline-flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5" /> Cambiar foto
            </button>
            {current && (
              <button type="button" onClick={() => onChange("")} className="btn-ghost px-3 py-1.5 text-xs rounded-sm">
                Volver al default
              </button>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75" onClick={() => setOpen(false)}>
          <div className="bg-surface border border-line max-w-4xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-fg font-semibold text-lg">Elegí la foto de portada</div>
                <div className="text-mute text-xs mt-1">Se mostrará a sangrada en el hero del sitio público.</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-mute hover:text-fg text-xs">Cerrar</button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {photos.map((p) => {
                const selected = p.filename === current;
                return (
                  <button key={p.id} type="button" onClick={() => { onChange(p.filename); setOpen(false); }}
                    className={`relative aspect-[4/3] overflow-hidden border ${selected ? "border-[#1E5FFF]" : "border-line"} hover:border-[#1E5FFF] transition-colors group`}>
                    <img src={photoUrl(p)} alt={p.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                    {selected && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-[#1E5FFF] flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                      <div className="text-fg text-[12px] truncate">{p.title || p.filename}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LogoUpload({ currentFilename, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.logo_filename);
      mutate("/public/home");
      toast.success("Logo subido correctamente");
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      toast.error("Error al subir el logo");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-xs text-[#9bb0e0] mb-2">Logo de marca</label>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] flex items-center justify-center overflow-hidden flex-shrink-0" style={{ borderRadius: 4 }}>
          {currentFilename
            ? <img src={logoUrl(currentFilename)} alt="logo" className="w-full h-full object-contain p-1" />
            : <span className="text-white font-bold text-lg">LU</span>
          }
        </div>
        <div className="flex items-center gap-2 flex-1">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" id="logo-file-input" onChange={upload} />
          <label
            htmlFor="logo-file-input"
            className={`btn-primary px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-medium cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Subiendo…" : currentFilename ? "Cambiar logo" : "Subir logo"}
          </label>
          {currentFilename && <span className="text-[#9bb0e0] text-xs truncate max-w-[140px]">{currentFilename}</span>}
        </div>
      </div>
      <p className="text-[#7da3ff] text-[11px] mt-2">PNG, SVG o JPG recomendado. Se mostrará en el header y footer del sitio.</p>
    </div>
  );
}

function FaviconUpload({ currentFilename, onUploaded }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/favicon", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.favicon_filename);
      mutate("/public/home");
      toast.success("Favicon subido correctamente");
      if (fileRef.current) fileRef.current.value = "";
    } catch {
      toast.error("Error al subir el favicon");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-xs text-[#9bb0e0] mb-2">Favicon (ícono del tab)</label>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 bg-[rgba(10,20,55,0.5)] border border-[rgba(120,160,255,0.18)] flex items-center justify-center overflow-hidden flex-shrink-0" style={{ borderRadius: 4 }}>
          {currentFilename
            ? <img src={faviconUrl(currentFilename)} alt="favicon" className="w-8 h-8 object-contain" />
            : <span className="text-[#3a5080] text-xs text-center leading-tight">sin favicon</span>
          }
        </div>
        <div className="flex items-center gap-2 flex-1">
          <input ref={fileRef} type="file" accept=".ico,.png,.jpg,.jpeg,.svg,image/*" className="hidden" id="favicon-file-input" onChange={upload} />
          <label
            htmlFor="favicon-file-input"
            className={`btn-primary px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-medium cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Subiendo…" : currentFilename ? "Cambiar favicon" : "Subir favicon"}
          </label>
          {currentFilename && <span className="text-[#9bb0e0] text-xs truncate max-w-[140px]">{currentFilename}</span>}
        </div>
      </div>
      <p className="text-[#7da3ff] text-[11px] mt-2">ICO o PNG 32×32 recomendado. Aparece en el tab del navegador.</p>
    </div>
  );
}
